import {
  useForm,
  SubmitHandler,
  FieldValues,
} from 'react-hook-form';
import { Modal, Button, Form } from 'react-bootstrap';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentUsernameForm } from '../types/user.types';

interface EditUsernameModalProps {
  show: boolean;
  currentUsername: string;
  saveUserInfo: (_result: boolean, _data: any) => void;
  usernameExist: boolean;
  setUserNameExist: (_exist: boolean) => void;
}

function EditUsernameModal({
  show,
  saveUserInfo,
  currentUsername,
  usernameExist,
  setUserNameExist,
}: EditUsernameModalProps): JSX.Element {
  const { t } = useTranslation();
  const submitRef = useRef<HTMLButtonElement>(null);
  const [showWarning, setShowWarning] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<CurrentUsernameForm>();

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    if (data.newUsername === data.updatedUsername) {
      saveUserInfo(true, {
        username: data.newUsername,
        type: 'username',
      });
      setShowWarning(false);
      setUserNameExist(false);
      reset();
    } else {
      setUserNameExist(false);
      setShowWarning(true);
    }
  };

  function handleClose(): void {
    setShowWarning(false);
    saveUserInfo(false, {});
    setUserNameExist(false);
    reset();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      className="modal"
      backdrop="static"
      size="lg"
    >
      <Modal.Header className="text-center" closeButton>
        <Modal.Title>{t('Update_Username')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showWarning && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Username_Update_Warning')}
          </div>
        )}
        {usernameExist && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Username_Exists')}
          </div>
        )}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <Form.Label>{t('Current_Username')}</Form.Label>
            <Form.Control
              value={currentUsername}
              readOnly
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('New_Username')}</Form.Label>
            <Form.Control
              type="text"
              {...register('newUsername', { required: true })}
              isInvalid={!!errors.newUsername}
            />
            {errors.newUsername && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('Confirm_Username')}</Form.Label>
            <Form.Control
              type="text"
              {...register('updatedUsername', { required: true })}
              isInvalid={!!errors.updatedUsername}
            />
            {errors.updatedUsername && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <button
            ref={submitRef}
            type="submit"
            style={{ display: 'none' }}
            aria-label="submit"
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleClose}>
          {t('PDP.Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="col-3"
          onClick={() => { submitRef.current?.click(); }}
        >
          {t('PDP.Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditUsernameModal;
